import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut, RotateCw, Check, X } from 'lucide-react';

interface PhotoCropDialogProps {
  open: boolean;
  imageFile: File | null;
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

export const PhotoCropDialog: React.FC<PhotoCropDialogProps> = ({ open, imageFile, onConfirm, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const CANVAS_SIZE = 280;

  useEffect(() => {
    if (!imageFile) return;
    const url = URL.createObjectURL(imageFile);
    setImageSrc(url);
    const img = new Image();
    img.onload = () => {
      setImageEl(img);
      // Fit image so it covers the circle
      const minDim = Math.min(img.width, img.height);
      const fitScale = CANVAS_SIZE / minDim;
      setScale(fitScale);
      setOffset({
        x: (CANVAS_SIZE - img.width * fitScale) / 2,
        y: (CANVAS_SIZE - img.height * fitScale) / 2,
      });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !imageEl) return;

    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw image
    ctx.save();
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(imageEl, offset.x, offset.y, imageEl.width * scale, imageEl.height * scale);
    ctx.restore();

    // Draw circle overlay guide
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'hsl(var(--primary))';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [imageEl, scale, offset]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    setDragging(true);
    setDragStart({ x: t.clientX - offset.x, y: t.clientY - offset.y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const t = e.touches[0];
    setOffset({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
  };

  const handleZoom = (val: number[]) => {
    if (!imageEl) return;
    const minDim = Math.min(imageEl.width, imageEl.height);
    const baseScale = CANVAS_SIZE / minDim;
    const newScale = baseScale * val[0];
    // Recenter on zoom
    const cx = CANVAS_SIZE / 2;
    const cy = CANVAS_SIZE / 2;
    const oldCx = (cx - offset.x) / scale;
    const oldCy = (cy - offset.y) / scale;
    setScale(newScale);
    setOffset({
      x: cx - oldCx * newScale,
      y: cy - oldCy * newScale,
    });
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create output at 256x256
    const outCanvas = document.createElement('canvas');
    outCanvas.width = 256;
    outCanvas.height = 256;
    const ctx = outCanvas.getContext('2d');
    if (!ctx || !imageEl) return;

    ctx.beginPath();
    ctx.arc(128, 128, 128, 0, Math.PI * 2);
    ctx.clip();

    const outScale = 256 / CANVAS_SIZE;
    ctx.drawImage(
      imageEl,
      offset.x * outScale,
      offset.y * outScale,
      imageEl.width * scale * outScale,
      imageEl.height * scale * outScale
    );

    outCanvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'client-photo.png', { type: 'image/png' });
      onConfirm(file);
    }, 'image/png', 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Position & Size Photo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground">Drag to position, zoom to fit</p>
          <div
            className="relative rounded-full overflow-hidden border-2 border-primary cursor-move bg-muted"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={() => setDragging(false)}
          >
            <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} className="w-full h-full" />
          </div>
          <div className="flex items-center gap-3 w-full max-w-[240px]">
            <ZoomOut className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Slider
              min={0.5}
              max={3}
              step={0.05}
              value={[imageEl ? scale / (CANVAS_SIZE / Math.min(imageEl.width, imageEl.height)) : 1]}
              onValueChange={handleZoom}
              className="flex-1"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </div>
        </div>
        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button onClick={handleConfirm}>
            <Check className="h-4 w-4 mr-1" /> Save Photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
